import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Mail, Clock, ArrowLeft, Sparkles } from 'lucide-react';
import * as THREE from 'three';

const ComingSoon: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrame = useRef<number>();

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !(window as any).WebGLRenderingContext ||
      !canvasRef.current
    ) {
      return;
    }

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
    });
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      uTime: { value: 0 },
    };

    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;

        float dither(vec2 p) {
          return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453);
        }

        void main() {
          vec2 uv = vUv;
          float noise = dither(uv * 128.0 + uTime * 0.01);

          float wave = sin((uv.x + uTime * 0.08) * 8.0) * 0.08;
          float pulse = smoothstep(0.0, 1.0, sin(uTime * 0.4) * 0.5 + 0.5);

          vec3 neonBlue = vec3(0.2, 0.6, 1.0);
          vec3 neonPurple = vec3(0.7, 0.3, 1.0);
          vec3 gradient = mix(neonPurple, neonBlue, uv.y + wave);

          float grid = smoothstep(0.48, 0.5, abs(fract(uv * 6.0 + pulse) - 0.5));
          grid *= (noise * 0.4 + 0.6);

          vec3 color = gradient + vec3(grid * 0.4);
          color += noise * 0.08;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      if (!canvasRef.current) return;
      const { clientWidth, clientHeight } = canvasRef.current;
      renderer.setSize(clientWidth, clientHeight, false);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    };

    const renderScene = (time: number) => {
      uniforms.uTime.value = time * 0.001;
      renderer.render(scene, camera);
      animationFrame.current = requestAnimationFrame(renderScene);
    };

    resize();
    window.addEventListener('resize', resize);
    renderScene(0);

    return () => {
      cancelAnimationFrame(animationFrame.current || 0);
      window.removeEventListener('resize', resize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/40 to-slate-950" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 blur-3xl opacity-40" />
            <div className="relative bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-3xl">
              <Scale className="h-16 w-16 text-cyan-300 mx-auto mb-4" />
              <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">
                Yakında
              </h1>
            </div>
          </div>
        </div>

        <div className="space-y-6 mb-12">
          <div className="flex items-center justify-center gap-3 text-cyan-300 mb-4">
            <Clock className="h-6 w-6" />
            <p className="text-xl md:text-2xl font-semibold">Yeni Özellikler Hazırlanıyor</p>
          </div>
          
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Avukasa ekibi olarak sizin için harika şeyler üzerinde çalışıyoruz. 
            Yeni özellikler ve iyileştirmeler yakında sizlerle olacak.
          </p>

          <div className="flex flex-wrap justify-center gap-3 pt-6">
            {['Yeni Özellikler', 'Performans İyileştirmeleri', 'Kullanıcı Deneyimi'].map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm uppercase tracking-widest text-slate-200"
              >
                <Sparkles className="h-4 w-4 text-cyan-300" />
                {badge}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl max-w-md mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Mail className="h-5 w-5 text-cyan-300" />
              <h3 className="text-lg font-semibold text-white">Bildirim Alın</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Hazır olduğunda size haber vermemizi ister misiniz?
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="E-posta adresiniz"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
              <button className="bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-900 font-semibold px-6 py-2 rounded-lg hover:opacity-90 transition-opacity">
                Kaydol
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Ana Sayfaya Dön
            </Link>
            <span className="text-slate-500 hidden sm:inline">•</span>
            <Link
              to="/login"
              className="bg-white/10 text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors"
            >
              Giriş Yap
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;

